import { HexOutline } from '../brand/Hexagon';
import { Reveal } from '../ui/Reveal';
import { Eyebrow, SectionTitle } from '../ui/SectionHeading';
import { valueProps } from '../../content/site';
import type { RoleCard } from '../../content/site';
import { externalLinkAttrs, sanitizeHref } from '../../lib/url';
import { usePendingLink } from '../ui/ComingSoon';

function Card({ card, blue }: { card: RoleCard; blue: boolean }) {
  const pendingLink = usePendingLink();

  return (
    <article
      className={`group relative overflow-hidden rounded-card p-[clamp(36px,4vw,52px)] transition-[transform,box-shadow] duration-350 hover:-translate-y-1.5 ${
        blue
          ? 'bg-brand text-white hover:shadow-card-blue'
          : 'border border-line bg-white hover:border-line-hover hover:shadow-card-white'
      }`}
    >
      {blue && (
        <HexOutline
          className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 text-white/12"
        />
      )}

      <p
        className={`font-display text-xs font-semibold tracking-[0.24em] ${
          blue ? 'text-white/70' : 'text-brand'
        }`}
      >
        {card.kicker}
      </p>

      <h3
        className={`mt-3.5 font-display text-[clamp(1.35rem,2.2vw,1.75rem)] font-extrabold uppercase leading-tight ${
          blue ? 'text-white' : 'text-ink'
        }`}
      >
        {card.title}
      </h3>

      <p
        className={`mt-4 text-[15.5px] leading-[1.7] ${
          blue ? 'text-white/85' : 'text-ink-muted'
        }`}
      >
        {card.description}
      </p>

      <ul className="mt-8">
        {card.benefits.map((benefit) => (
          <li
            key={benefit}
            className={`border-t py-4 text-[15px] leading-[1.65] ${
              blue
                ? 'border-white/22 text-white/90'
                : 'border-line text-ink-muted'
            }`}
          >
            {benefit}
          </li>
        ))}
      </ul>

      <a
        href={sanitizeHref(card.href)}
        {...externalLinkAttrs(card.href)}
        {...pendingLink(card.href, card.cta)}
        className={`mt-7 inline-block border-b pb-1 font-display text-[15px] font-semibold transition-colors ${
          blue
            ? 'border-white/50 text-white hover:border-white'
            : 'border-brand/40 text-brand hover:border-brand'
        }`}
      >
        {card.cta}
        {/* La flecha es decorativa: fuera del nombre accesible del enlace, que
            si no se anunciaría como "Publicar una actuación flecha derecha". */}
        <span aria-hidden="true"> →</span>
      </a>
    </article>
  );
}

export function ValueProps() {
  return (
    <section id="propuesta" className="section-y" aria-labelledby="propuesta-titulo">
      <div className="shell">
        <Reveal>
          <Eyebrow>{valueProps.eyebrow}</Eyebrow>
          <SectionTitle className="mt-6 max-w-[820px]" >
            <span id="propuesta-titulo">{valueProps.title}</span>
          </SectionTitle>
          <p className="mt-6 max-w-[620px] text-[16px] leading-[1.75] text-ink-muted">
            {valueProps.intro}
          </p>
        </Reveal>

        <div className="mt-14 grid gap-7 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
          {valueProps.cards.map((card, index) => (
            <Reveal key={card.title} delay={index === 0 ? 0 : 1}>
              <Card card={card} blue={index === 0} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
